import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Avatar,
} from '@mui/material';
import {
  DirectionsCar,
  Home,
  Business,
  Favorite,
  Security,
  Speed,
  CheckCircle,
  Star,
} from '@mui/icons-material';

const Homepage: React.FC = () => {
  const handleGetQuote = () => {
    // Navigate to quote page
    console.log('Navigate to quote page');
  };

  const handleFileClaim = () => {
    // Navigate to claims application
    window.location.href = '/claims';
  };

  const insuranceProducts = [
    {
      icon: <DirectionsCar sx={{ fontSize: 48, color: '#5f0397' }} />,
      title: 'Auto Insurance',
      description: 'Car insurance that\'s mostly comprehensive (and completely awesome)',
      features: ['Comprehensive coverage options', 'Multi-state benefits', 'Lightning-fast digital claims processing'],
    },
    {
      icon: <Home sx={{ fontSize: 48, color: '#5f0397' }} />,
      title: 'Home Insurance', 
      description: 'Protecting your castle with mostly foolproof coverage',
      features: ['Property protection that works', 'Natural disaster coverage', 'Seamless digital document management'],
    },
    {
      icon: <Business sx={{ fontSize: 48, color: '#5f0397' }} />,
      title: 'Business Insurance',
      description: 'Commercial insurance for when business gets mostly complicated',
      features: ['Commercial liability coverage', 'Professional indemnity', 'Revolutionary digital document management'],
    },
    {
      icon: <Favorite sx={{ fontSize: 48, color: '#5f0397' }} />,
      title: 'Life Insurance',
      description: 'Life insurance that\'s mostly about living worry-free',
      features: ['Term and whole life options', 'Simplified beneficiary management', 'Fort Knox-level secure document storage'],
    },
  ];

  const whyChooseUs = [
    {
      icon: <Security sx={{ fontSize: 32, color: '#1A92C0' }} />,
      title: 'Mostly Secure',
      description: 'State-of-the-art security that\'s actually completely reliable',
    },
    {
      icon: <Speed sx={{ fontSize: 32, color: '#1A92C0' }} />,
      title: 'Lightning Fast Claims',
      description: 'Claims processed at mostly lightning speed (usually same day!)',
    },
    {
      icon: <CheckCircle sx={{ fontSize: 32, color: '#1A92C0' }} />,
      title: 'Mostly Satisfied Customers',
      description: '500,000+ customers who are mostly happy with our service',
    },
  ];

  const testimonials = [
    {
      text: "Mostly Safe processed my claim in record time! I was mostly impressed with their digital platform.",
      author: "Sarah M.",
      rating: 5,
    },
    {
      text: "Their customer service is mostly outstanding. The claims process was surprisingly smooth.",
      author: "Mike R.",
      rating: 4,
    },
    {
      text: "I'm mostly satisfied with their coverage. Great value for mostly reliable service.",
      author: "Jennifer L.",
      rating: 5,
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #5f0397 0%, #1A92C0 100%)',
          color: 'white',
          py: 12,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ mb: 2 }}>
            Mostly Safe, Completely Reliable™
          </Typography>
          <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 400, opacity: 0.9 }}>
            We've got you covered... most of the time!
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mb: 6, fontWeight: 400, opacity: 0.8 }}>
            Join our 500,000+ mostly satisfied customers • 36+ years of mostly reliable service
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGetQuote}
              sx={{
                backgroundColor: '#1A92C0',
                fontSize: '1.2rem',
                px: 4,
                py: 2,
                '&:hover': { backgroundColor: '#136B8C' },
              }}
            >
              Get Mostly Safe Today!
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleFileClaim}
              sx={{
                borderColor: 'white',
                color: 'white',
                fontSize: '1.2rem',
                px: 4,
                py: 2,
                '&:hover': { 
                  borderColor: 'white', 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)' 
                },
              }}
            >
              Experience Lightning-Fast Claims
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Insurance Products Section */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Typography variant="h2" align="center" gutterBottom sx={{ mb: 2 }}>
          Our Insurance Products
        </Typography>
        <Typography variant="h6" align="center" gutterBottom sx={{ mb: 8, color: '#535353' }}>
          Mostly comprehensive coverage for all your needs
        </Typography>

        <Grid container spacing={4}>
          {insuranceProducts.map((product, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  textAlign: 'center',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    transition: 'transform 0.3s ease-in-out',
                    boxShadow: '0 8px 25px rgba(95, 3, 151, 0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    {product.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom>
                    {product.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                    {product.description}
                  </Typography>
                  <Box>
                    {product.features.map((feature, featureIndex) => (
                      <Chip
                        key={featureIndex}
                        label={feature}
                        size="small"
                        sx={{ 
                          m: 0.5,
                          backgroundColor: 'rgba(95, 3, 151, 0.1)',
                          color: '#5f0397',
                          fontSize: '0.75rem',
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Why Choose Us Section */}
      <Box sx={{ backgroundColor: '#fafafa', py: 12 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom sx={{ mb: 2 }}>
            Why Choose Mostly Safe?
          </Typography>
          <Typography variant="h6" align="center" gutterBottom sx={{ mb: 8, color: '#535353' }}>
            Because we're mostly awesome at what we do
          </Typography>

          <Grid container spacing={4}>
            {whyChooseUs.map((reason, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {reason.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom>
                    {reason.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {reason.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Typography variant="h2" align="center" gutterBottom sx={{ mb: 2 }}>
          What Our Mostly Satisfied Customers Say
        </Typography>
        <Typography variant="h6" align="center" gutterBottom sx={{ mb: 8, color: '#535353' }}>
          Real reviews from real people (mostly)
        </Typography>

        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper 
                sx={{ 
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Box sx={{ mb: 2 }}>
                    {[...Array(testimonial.rating)].map((_, starIndex) => (
                      <Star key={starIndex} sx={{ color: '#ffc107', fontSize: 20 }} />
                    ))}
                  </Box>
                  <Typography variant="body1" gutterBottom sx={{ fontStyle: 'italic', mb: 3 }}>
                    "{testimonial.text}"
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: '#5f0397' }}>
                    {testimonial.author.charAt(0)}
                  </Avatar>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {testimonial.author}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #470271 0%, #5f0397 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom>
            Join the Mostly Satisfied!
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ mb: 4, opacity: 0.9 }}>
            Get your quote today and discover mostly amazing coverage
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleGetQuote}
            sx={{
              backgroundColor: '#1A92C0',
              fontSize: '1.2rem',
              px: 6,
              py: 2,
              '&:hover': { backgroundColor: '#136B8C' },
            }}
          >
            Discover Mostly Amazing Coverage
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default Homepage;